import { Injectable } from '@angular/core';

import { combineLatest, merge, Observable, of, Subject } from 'rxjs';
import { map, catchError, filter, switchMap, startWith, tap, distinctUntilChanged } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import { getDestinationInCampaign } from '@fcom/core/utils/cms-data-utils';
import { LanguageService } from '@fcom/ui-translate';
import { CmsCampaign, CmsDataService, ConfigService, GlobalBookingTravelClass } from '@fcom/core';
import { CmsPlaceholder } from '@fcom/core-api';
import { MarketingOffer, DestinationSearchBlockOffer, MarketingOfferPrices } from '@fcom/cms/interfaces';
import { diff, textMatch, isNotEmpty, isPresent, TimeUnit } from '@fcom/core/utils';
import { NON_EUROPE_SITES } from '@fcom/core/constants';
import { TripType } from '@fcom/core/interfaces';
import { mapToMarketingOffers } from '@fcom/cms/utils';
import { mapToAmDestinationOffer } from '@fcom/common/utils';
import { InstantsearchService } from '@fcom/dapi/api/services';
import { CheapestPriceForAllDestinationsService } from '@fcom/cms/services';
import { finShare, snapshot } from '@fcom/rx';
import { DestinationSearchService } from '@fcom/destination-search/services/destination-search.service';
import { flattenDestinations } from '@fcom/destination-search/utils/flatten';
import {
  DestinationSearchFeatureState,
  DestinationSearchItem,
  PriceLoadingState,
  DestinationSearchTripDates,
  DestinationSearchItemType,
  DestinationSearchQueryParamNames,
} from '@fcom/destination-search/interfaces';
import { AmDestinationService } from '@fcom/destination-search/services/am-destination.service';

import { travelClassState, tripDatesState, tripTypeState, selectedDestinationsState } from '../store';
import { Continent } from '../interfaces';

@Injectable()
export class DestinationSearchOffersService {
  public readonly loaderStatus$: Observable<PriceLoadingState>;
  private status$: Subject<PriceLoadingState> = new Subject();
  private campaigns$: Observable<CmsCampaign[]>;

  constructor(
    private configService: ConfigService,
    private instantsearchService: InstantsearchService,
    private cheapestPriceForAllDestinationsService: CheapestPriceForAllDestinationsService,
    private destinationSearchService: DestinationSearchService,
    private store$: Store<DestinationSearchFeatureState>,
    private cmsDataService: CmsDataService,
    private languageService: LanguageService,
    private amDestinationService: AmDestinationService
  ) {
    this.loaderStatus$ = this.status$.pipe(startWith(PriceLoadingState.INITIAL));
    this.campaigns$ = this.cmsDataService.getOngoingCampaigns(
      this.languageService.langValue,
      this.languageService.localeValue
    );
  }

  /**
   * Map prices to destination information
   */
  public getDestinationsWithOffers(
    restrictions: DestinationSearchQueryParamNames,
    identifier: string,
    content: CmsPlaceholder,
    selectedOriginLocationCode: string
  ): Observable<DestinationSearchBlockOffer[]> {
    const selectedTripDates$ = this.store$.pipe(tripDatesState(identifier));
    const selectedTripType$ = this.store$.pipe(tripTypeState(identifier));
    const selectedTravelClass$ = this.store$.pipe(travelClassState(identifier));
    const filteredDestinations$ = combineLatest([
      this.destinationSearchService.destinationSearch(restrictions),
      this.store$.pipe(selectedDestinationsState(identifier)),
    ]).pipe(
      distinctUntilChanged(),
      map(([destinations, destinationFilters]) => {
        const allDestinations = flattenDestinations(destinations).filter(
          (d) => d.type === DestinationSearchItemType.DESTINATION
        );
        const matchingDestinations = allDestinations.filter((destination: DestinationSearchItem) =>
          this.destinationMatchesFilters(destinationFilters, destination)
        );
        return isNotEmpty(matchingDestinations) ? matchingDestinations : allDestinations;
      })
    );

    const offersWithDates$ = this.getOffersWithDates(
      selectedTripDates$,
      selectedTripType$,
      filteredDestinations$,
      selectedOriginLocationCode
    );

    const allOffers$ = this.getAllOffers(
      selectedTripDates$,
      selectedTripType$,
      offersWithDates$,
      selectedOriginLocationCode
    );

    // enable flight+hotel on finnish market
    const amDestinationList$ = this.languageService.localeValue.includes('FI')
      ? this.amDestinationService.amDestinationList()
      : of([]);

    return combineLatest([
      filteredDestinations$,
      allOffers$,
      selectedTravelClass$,
      this.campaigns$,
      amDestinationList$,
      selectedTripType$,
    ]).pipe(
      map(([destinations, offers, travelClass, campaigns, amDestinationList]) => {
        const offersMap: { [locationCode: string]: MarketingOffer } = offers.reduce((allOffers, offer) => {
          const amDestination = amDestinationList.find(({ dest }) => dest === offer.airportCode);
          return {
            ...allOffers,
            [offer.destination]: {
              ...offer,
              amDestination: amDestination ? mapToAmDestinationOffer(amDestination) : null,
            },
          };
        }, {});

        return destinations
          .map((destination: DestinationSearchItem): DestinationSearchBlockOffer => {
            const matchingOffer: MarketingOffer = offersMap[destination.locationCode];
            return this.mapToBlockOffer(destination, travelClass, matchingOffer, campaigns, selectedOriginLocationCode);
          })
          .filter((item) => content?.localSettings?.showDestinationsWithoutPrice || item.price);
      })
    );
  }

  public forceLoadingScreen(duration: number): void {
    if (snapshot(this.status$) === PriceLoadingState.LOADING) {
      return;
    }
    this.status$.next(PriceLoadingState.LOADING);
    setTimeout(() => {
      this.status$.next(PriceLoadingState.LOADED);
    }, duration);
  }

  /**
   * Fetch prices in case dates are selected
   */
  private getOffersWithDates(
    selectedDates$: Observable<DestinationSearchTripDates>,
    tripType$: Observable<TripType>,
    filteredDestinations$: Observable<DestinationSearchItem[]>,
    selectedOriginLocationCode: string
  ): Observable<MarketingOffer[]> {
    return combineLatest([selectedDates$, tripType$, filteredDestinations$]).pipe(
      filter(
        ([selectedDates, tripType]) =>
          (tripType === TripType.RETURN &&
            isPresent(selectedDates?.departureDate) &&
            isPresent(selectedDates?.returnDate)) ||
          (tripType === TripType.ONEWAY && isPresent(selectedDates?.departureDate))
      ),
      switchMap(([selectedDates, tripType, destinations]) => {
        const locationCodes = destinations
          .filter((destination) => destination.locationCode)
          .map((destination) => destination.locationCode);

        let params = {
          departureLocationCodes: [selectedOriginLocationCode],
          destinationLocationCodes: locationCodes,
          numberOfDays: 0,
          travelClasses: [
            GlobalBookingTravelClass.ECONOMY.toLowerCase(),
            GlobalBookingTravelClass.ECOPREMIUM.toLowerCase(),
            GlobalBookingTravelClass.BUSINESS.toLowerCase(),
          ],
        };

        const isReturnWithBothDates =
          tripType === TripType.RETURN &&
          isPresent(selectedDates?.departureDate) &&
          isPresent(selectedDates?.returnDate);
        const isOnewayWithOneDate = tripType === TripType.ONEWAY && isPresent(selectedDates?.departureDate);
        if (isReturnWithBothDates || isOnewayWithOneDate) {
          const departureDate = selectedDates.departureDate.toString();
          const returnDate = selectedDates.returnDate?.toString();

          params = Object.assign({}, params, {
            departureDate,
            lengthOfStay: isOnewayWithOneDate ? 1 : diff(new Date(departureDate), new Date(returnDate), TimeUnit.day),
            tripTypes: [tripType],
            locale: this.languageService.langValue,
          });
        }

        this.status$.next(PriceLoadingState.LOADING);

        return this.instantsearchService
          .getPricesForFlights(this.configService.cfg.instantSearchUrl, params)
          .pipe(catchError(() => of([])));
      }),
      map(mapToMarketingOffers),
      tap(() => this.status$.next(PriceLoadingState.LOADED)),
      finShare()
    );
  }

  private getAllOffers(
    selectedDates$: Observable<DestinationSearchTripDates>,
    tripType$: Observable<TripType>,
    offersWithDates$: Observable<MarketingOffer[]>,
    selectedOriginLocationCode: string
  ): Observable<MarketingOffer[]> {
    return merge(
      combineLatest([selectedDates$, tripType$]).pipe(
        filter(
          ([selectedDates, tripType]) =>
            (tripType === TripType.RETURN &&
              !isPresent(selectedDates?.departureDate) &&
              !isPresent(selectedDates?.returnDate)) ||
            (tripType === TripType.ONEWAY && !isPresent(selectedDates?.departureDate))
        ),
        switchMap(([, tripType]) => {
          this.status$.next(PriceLoadingState.LOADING);
          if (!NON_EUROPE_SITES.includes(this.languageService.langValue)) {
            return this.cheapestPriceForAllDestinationsService.bothWayOffers(selectedOriginLocationCode);
          }
          return tripType === TripType.ONEWAY
            ? //oneway
              this.cheapestPriceForAllDestinationsService.oneWayOffers(selectedOriginLocationCode)
            : //return
              this.cheapestPriceForAllDestinationsService.bothWayOffers(selectedOriginLocationCode);
        }),
        tap(() => this.status$.next(PriceLoadingState.LOADED))
      ),
      offersWithDates$
    );
  }

  private mapToBlockOffer(
    destination: DestinationSearchItem,
    selectedTravelClass: GlobalBookingTravelClass,
    matchingOffer?: MarketingOffer,
    campaigns?: CmsCampaign[],
    selectedOriginLocationCode?: string
  ): DestinationSearchBlockOffer {
    const matchingEconomyOffer = matchingOffer?.prices.find(
      (t) => t.travelClass.toUpperCase() === GlobalBookingTravelClass.ECONOMY
    );
    const matchingPremiumEconomyOffer = matchingOffer?.prices.find(
      (t) => t.travelClass.toUpperCase() === GlobalBookingTravelClass.ECOPREMIUM
    );
    const matchingBusinessOffer = matchingOffer?.prices.find(
      (t) => t.travelClass.toUpperCase() === GlobalBookingTravelClass.BUSINESS
    );

    const showAllOffers = selectedTravelClass === GlobalBookingTravelClass.MIXED;
    const showEconomyOffer = selectedTravelClass === GlobalBookingTravelClass.ECONOMY;
    const showPremiumEconomyOffer = selectedTravelClass === GlobalBookingTravelClass.ECOPREMIUM;
    const showBusinessOffer = selectedTravelClass === GlobalBookingTravelClass.BUSINESS;

    const marketingOffer = {
      destination: destination.title,
      origin: destination.origin,
      prices: [
        ...this.getOfferPrice(
          matchingEconomyOffer,
          showAllOffers || showEconomyOffer,
          GlobalBookingTravelClass.ECONOMY
        ),
        ...this.getOfferPrice(
          matchingPremiumEconomyOffer,
          showAllOffers || showPremiumEconomyOffer,
          GlobalBookingTravelClass.ECOPREMIUM
        ),
        ...this.getOfferPrice(
          matchingBusinessOffer,
          showAllOffers || showBusinessOffer,
          GlobalBookingTravelClass.BUSINESS
        ),
      ],
    };

    const onewayPrice = this.getPriceByTripTypeAndTravelClass(matchingOffer, TripType.ONEWAY, selectedTravelClass);
    const returnPrice = this.getPriceByTripTypeAndTravelClass(matchingOffer, TripType.RETURN, selectedTravelClass);

    return {
      title: destination.title,
      link: destination.url,
      imageData: destination.picture,
      destination: destination.title,
      // TODO: check which price is actually the lowest instead of relying on it being the first...
      price: marketingOffer.prices[0]?.price?.amount
        ? Math.ceil(Number(marketingOffer.prices[0].price.amount))
        : undefined,
      onewayPrice,
      returnPrice,
      origin: matchingOffer ? matchingOffer.origin : undefined,
      flag: destination.countryCode ? destination.countryCode.toLowerCase() : undefined,
      countryName: destination.country || undefined,
      currency: destination.currency,
      titleMaster: destination.titleMaster,
      marketingOffer: marketingOffer.prices.length ? marketingOffer : undefined,
      continent: destination.continentMaster,
      badgeIcon: undefined,
      badgeLabel: campaigns.find(
        (campaign) =>
          (campaign?.destinations?.some((dest: string) => dest === destination.locationCode) ||
            campaign?.destinationsByOrigin?.[selectedOriginLocationCode]?.some(
              (dest: string) => dest === destination.locationCode
            )) &&
          campaign?.campaignTravelClass === selectedTravelClass
      )?.campaignLabel,
      enableBlackTeaser: getDestinationInCampaign(campaigns, destination.locationCode)?.enableBlackTeaser,
      amDestination: matchingOffer?.amDestination,
      locationCode: destination.locationCode,
      directFlight: destination.directFlight,
      isShortHaul: NON_EUROPE_SITES.includes(this.languageService.langValue)
        ? false
        : destination.continentMaster === Continent.EUROPE,
    };
  }

  private getPriceByTripTypeAndTravelClass(
    marketingOffer: MarketingOffer,
    tripType: TripType,
    travelClass: GlobalBookingTravelClass
  ): MarketingOfferPrices | undefined {
    return marketingOffer?.prices.find(
      (price) =>
        (travelClass === GlobalBookingTravelClass.MIXED || price.travelClass.toUpperCase() === travelClass) &&
        price.price?.tripType === tripType
    );
  }

  private getOfferPrice(
    offer: MarketingOfferPrices,
    showOffer: boolean,
    travelClass: GlobalBookingTravelClass
  ): MarketingOfferPrices[] {
    return offer && showOffer
      ? [
          {
            travelClass,
            price: {
              currencyCode: offer.price.currencyCode,
              amount: `${Math.ceil(Number(offer.price.amount))}`,
            },
          },
        ]
      : [];
  }

  private destinationMatchesFilters(filters: DestinationSearchItem[], destination: DestinationSearchItem): boolean {
    return filters?.some(
      (f) =>
        (f.type === DestinationSearchItemType.DESTINATION && textMatch(f.locationCode, destination?.locationCode)) ||
        (f.type === DestinationSearchItemType.COUNTRY && textMatch(f.countryCode, destination?.countryCode)) ||
        (f.type === DestinationSearchItemType.CONTINENT && textMatch(f.titleMaster, destination?.continentMaster))
    );
  }
}
